// client/src/utils/ReliableCallManager.js
// Ported from 3cx_testing old/frontend/src/utils/callManager.js
// Manages call state using the reliable 3CX event logic

const STORAGE_KEY = 'bzct_reliable_calls_v1';

class ReliableCallManager {
    constructor() {
        this.calls = new Map();
        this.userExtension = null;
        this._hydrate();
    }

    _hydrate() {
        try {
            if (typeof window === 'undefined' || !window.localStorage) return;
            const raw = window.localStorage.getItem(STORAGE_KEY);
            if (!raw) return;
            const parsed = JSON.parse(raw);
            if (!Array.isArray(parsed?.calls)) return;
            for (const [callid, call] of parsed.calls) {
                const participants = new Map(
                    Array.isArray(call.participants) ? call.participants : []
                );
                this.calls.set(callid, { ...call, participants });
            }
        } catch (err) {
            console.warn('ReliableCallManager: failed to hydrate from storage', err);
        }
    }

    /**
     * Reconcile a server-side snapshot 
     *
     
     *
     * @param {Object} snapshot - { calls: [[callid, { ...call, participants: [...] }], ...] }
     */
    mergeSnapshot(snapshot) {
        if (!snapshot || !Array.isArray(snapshot.calls)) return;
        for (const [callid, call] of snapshot.calls) {
            const participants = new Map(
                Array.isArray(call.participants) ? call.participants : []
            );
            this.calls.set(String(callid), { ...call, participants });
        }
        this._persist();
    }

    _persist() {
        try {
            if (typeof window === 'undefined' || !window.localStorage) return;
            const serializable = {
                calls: Array.from(this.calls.entries()).map(([callid, call]) => [
                    callid,
                    {
                        ...call,
                        participants: Array.from(call.participants.entries())
                    }
                ])
            };
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(serializable));
        } catch (err) {
            console.warn('ReliableCallManager: failed to persist to storage', err);
        }
    }

    /**
     * Set current user extension to filter calls
     * @param {String|Number} extension 
     */
    setUserExtension(extension) {
        this.userExtension = extension ? String(extension) : null;
        // console.log(`📞 ReliableCallManager: Filter set to extension ${this.userExtension}`);
    }

    /**
     * Adds or updates a participant in the call map
     * @param {Object} callData - The reliable_participant_data message data
     */
    addOrUpdateParticipant(callData) {
        const {
            sequence,
            event_type,
            participant_data
        } = callData;

        if (!participant_data) return { action: 'ignored' };

        const {
            callid,
            status,
            party_caller_id,
            party_did,
            party_caller_name,
            party_dn,
            id: participant_id,
            dn
        } = participant_data;

        const callidStr = String(callid);
        // Unique key for participant within a call
        const participantKey = `${party_caller_id || 'unknown'}:${party_did || 'unknown'}`;

        // Determine actual status based on event_type 1 (Ended)
        let actualStatus = status;
        if (event_type === 1) {
            actualStatus = 'Ended';
        }

        // Initialize call entry if new
        if (!this.calls.has(callidStr)) {
            this.calls.set(callidStr, {
                callid: callidStr,
                participants: new Map(),
                firstSeen: new Date().toISOString(),
                everConnected: false,
                connectedAt: null,
                endedAt: null
            });
            // console.log(`✨ Reliable: Created new call: ${callidStr}`);
        }

        const call = this.calls.get(callidStr);
        const existingParticipant = call.participants.get(participantKey);

        // Preserve DN from earlier status if current one is missing
        let preservedDn = dn;
        if (!preservedDn && existingParticipant?.dn) {
            preservedDn = existingParticipant.dn;
        }

        const participantData = {
            participantKey,
            sequence,
            status: actualStatus,
            event_type,
            party_caller_id: party_caller_id || existingParticipant?.party_caller_id,
            party_did: party_did || existingParticipant?.party_did,
            party_caller_name: party_caller_name || existingParticipant?.party_caller_name,
            party_dn: party_dn || existingParticipant?.party_dn,
            participant_id: participant_id || existingParticipant?.participant_id,
            dn: preservedDn,
            lastUpdate: new Date().toISOString(),
            updates: existingParticipant ? existingParticipant.updates + 1 : 1
        };

        // Update everConnected and connectedAt if this participant shows connection
        if (actualStatus === 'Connected' || actualStatus === 'Talking') {
            if (!call.everConnected) {
                call.connectedAt = Date.now();
                call.everConnected = true;
            }
        }

        // Keep track of when the call ended
        if (actualStatus === 'Ended' && !call.endedAt) {
            call.endedAt = Date.now();
        }

        call.participants.set(participantKey, participantData);
        call.lastUpdate = new Date().toISOString();

        this._persist();
        return { action: existingParticipant ? 'updated' : 'added', call };
    }

    /**
     * Handle the reliable_call_ended event
     * @param {Object} data - The reliable_call_ended message data
     */
    handleCallEnded(data) {
        const { entity, sequence } = data;

        // Extract info from entity: /callcontrol/{ext}/participants/{id}
        const match = entity?.match(/\/callcontrol\/(\d+)\/participants\/(\d+)/);
        if (!match) return null;

        const userExt = match[1];
        const participantId = match[2];

        // console.log(`🛑 Reliable End: Extracted ext ${userExt}, participant ${participantId}`);

        // If this end event isn't for our extension, skip
        if (this.userExtension && userExt !== this.userExtension) return null;

        let foundCallId = null;
        for (const [callid, call] of this.calls) {
            for (const participant of call.participants.values()) {
                if (String(participant.participant_id) === String(participantId)) {
                    foundCallId = callid;

                    // Force update status to Ended
                    participant.status = 'Ended';
                    participant.event_type = 1;
                    participant.lastUpdate = new Date().toISOString();
                    call.lastUpdate = new Date().toISOString();
                    if (!call.endedAt) {
                        call.endedAt = Date.now();
                    }
                    // console.log(`✓ Reliable End: Marked call ${callid} as Ended`);
                    break;
                }
            }
            if (foundCallId) break;
        }
        if (foundCallId) this._persist();
        return foundCallId;
    }

    /**
     * Returns a list of active calls relevant to the current user
     */
    getActiveCalls() {
        const result = [];
        if (!this.userExtension) return result;

        for (const [callid, call] of this.calls) {
            const allParticipants = Array.from(call.participants.values());

            // Find the participant entry corresponding to our extension
            const userParticipant = allParticipants.find(p => String(p.dn) === this.userExtension);

            if (!userParticipant) continue;

            // Find the caller participant (different DN and has caller ID)
            const callerParticipant = allParticipants.find(p =>
                String(p.dn) !== this.userExtension && p.party_caller_id
            ) || userParticipant;

            // Determine the from number
            const fromNumber = userParticipant.party_caller_id || callerParticipant.party_caller_id;
            const callStatus = userParticipant.status;

            // Filter for standard UI statuses
            if (['Ringing', 'Connected', 'Ended', 'Talking'].includes(callStatus)) {
                // Map 'Talking' back to 'Connected' for UI consistency if needed
                const mappedStatus = callStatus === 'Talking' ? 'Connected' : callStatus;

                // Calculate duration if ended
                let callDuration = null;
                if (call.connectedAt && call.endedAt) {
                    callDuration = Math.round((call.endedAt - call.connectedAt) / 1000);
                }

                result.push({
                    callId: callid,
                    phoneNumber: fromNumber,
                    status: mappedStatus,
                    callerName: userParticipant.party_caller_name || callerParticipant.party_caller_name || fromNumber,
                    firstSeen: call.firstSeen,
                    startTime: call.connectedAt, // Timestamp for live timer
                    callDuration: callDuration, // Actual duration in seconds for ended calls
                    lastUpdate: call.lastUpdate,
                    participants: allParticipants,
                    isReliable: true, // Flag to distinguish
                    timerStarted: call.everConnected,
                    callEnded: mappedStatus === 'Ended',
                    endedAt: call.endedAt, // Expose for cleanup logic
                    isUnanswered: mappedStatus === 'Ended' && !call.everConnected
                });
            }
        }
        return result;
    }

    /**
     * Remove a call from the manager
     */
    removeCall(callid) {
        this.calls.delete(String(callid));
        this._persist();
    }

    /**
     * Clear calls. If `keepCallIds` is provided, calls whose id is in the set
     * are preserved (e.g. a call that is still in progress when the user hits
     * "Clear"); everything else is removed. With no argument, clears all.
     * @param {Set<String>} [keepCallIds]
     */
    clearAll(keepCallIds = null) {
        if (keepCallIds && keepCallIds.size > 0) {
            for (const callid of Array.from(this.calls.keys())) {
                if (!keepCallIds.has(callid)) {
                    this.calls.delete(callid);
                }
            }
            this._persist();
            return;
        }

        this.calls.clear();
        try {
            if (typeof window !== 'undefined' && window.localStorage) {
                window.localStorage.removeItem(STORAGE_KEY);
            }
        } catch {
            /* noop */
        }
    }
}

export const reliableCallManager = new ReliableCallManager();
